/**
 * spreadsheetApi — preview + import endpoints for the spreadsheet connector.
 *
 * V1 source-of-truth: ``ando-frontend/app/api/enterprise.ts`` (the
 * ``previewSpreadsheet`` + ``importSpreadsheet`` pair, lines 300-385).
 *
 * Three input modes (the BE validates exactly-one-set):
 *
 *   1. ``file``                       → local upload (XLSX / XLS / CSV)
 *   2. ``sourceFileId`` + ``sourceProvider="google_drive"``
 *                                     → cloud download, borrows the user's
 *                                       Drive token from ``user_connections``
 *   3. ``sourceFileId`` + ``sourceProvider="onedrive"``
 *                                     → same shape, OneDrive token
 *
 * Both endpoints accept multipart/form-data ONLY — sending JSON triggers a
 * 415. Field names are FROZEN to match the BE FastAPI signature
 * (snake_case: ``connector_name``, ``source_file_id``, ``source_provider``,
 * ``source_connection_key``, ``sheet_names``). Diverging here means the
 * BE silently ignores the form-field and falls back to defaults — see
 * ``feedback_v1_be_form_field_naming.md`` in user memory.
 *
 * Multi-sheet selection: ``sheetNames`` is JOINED with a single comma at
 * the wire boundary (V1 contract). The BE splits on ``,`` server-side.
 *
 * Preview pipeline (May 28, 2026 — V2 parity):
 *
 *   - ``previewSpreadsheet`` runs BEFORE import to parse the workbook
 *     out-of-band: returns one ``SpreadsheetPreviewSheet`` per worksheet
 *     with row counts, columns (name + dtype), and up to 5 sample rows.
 *   - The dialog hands this to the user so they can pick exactly which
 *     sheets to import (Excel multi-sheet) and see the inferred column
 *     types BEFORE committing — saves a wrong-format / wrong-sheet
 *     round-trip + the BE-side discovery wait.
 *   - ``warnings`` surfaces non-fatal issues (e.g. duplicate column
 *     names auto-renamed, empty rows trimmed) so the user can confirm
 *     the inferred schema is acceptable.
 */

import { apiClient } from "@ando/core/api";

export type SpreadsheetSourceProvider = "google_drive" | "onedrive";

// ─── Preview ────────────────────────────────────────────────────────────────

/** Per-column metadata parsed by the BE preview. */
export interface SpreadsheetPreviewColumn {
  readonly name: string;
  readonly original_name: string;
  readonly type: string;
  readonly nullable: boolean;
  readonly sample_values: readonly unknown[];
}

/** Per-sheet metadata parsed by the BE preview. */
export interface SpreadsheetPreviewSheet {
  readonly name: string;
  readonly original_name: string;
  readonly columns: readonly SpreadsheetPreviewColumn[];
  /** Up to 5 sample rows — values aligned with ``columns`` order. */
  readonly preview_rows: readonly (readonly unknown[])[];
  readonly total_rows: number;
}

export interface SpreadsheetPreviewResponse {
  readonly sheets: readonly SpreadsheetPreviewSheet[];
  readonly warnings: readonly string[];
  /** Best-effort file size (only set for cloud-download mode). */
  readonly file_size_bytes?: number;
}

export interface PreviewSpreadsheetOptions {
  /** Upload-mode: local ``.xlsx`` / ``.xls`` / ``.csv``. */
  readonly file?: File;
  /** Cloud-mode: provider file id (Drive ``fileId`` / OneDrive ``driveItemId``). */
  readonly source_file_id?: string;
  /** Cloud-mode: which Drive provider holds the token to borrow. */
  readonly source_provider?: SpreadsheetSourceProvider;
  /** Cloud-mode: explicit connection key for multi-account users. */
  readonly source_connection_key?: string;
  /**
   * Presigned-mode: MinIO object key returned by ``POST /spreadsheet/upload-url``
   * after the FE PUT'd the file body directly to MinIO. Use this when the
   * file exceeds the GFE multipart cap (~32 MB on GKE) — same wire field
   * the import endpoint already accepts, so the FE can carry the SAME key
   * through preview → import without re-uploading (Jun 14, 2026).
   */
  readonly source_presigned_key?: string;
  /**
   * Optional sheet subset to preview. When set, only these sheets are
   * parsed (saves BE work for large workbooks). Comma-joined at the wire
   * boundary like ``importSpreadsheet``.
   */
  readonly sheet_names?: readonly string[];
}

/**
 * POST /enterprise/connectors/preview-spreadsheet.
 *
 * Built on ``apiClient.request<T>()`` (NOT ``.post()``) for the same
 * boundary reason as ``importSpreadsheet`` — multipart needs the browser
 * to inject its own boundary; ``.post()`` would clobber it with
 * ``application/json``.
 */
export async function previewSpreadsheet(
  options: PreviewSpreadsheetOptions,
): Promise<SpreadsheetPreviewResponse> {
  const form = new FormData();

  if (options.file !== undefined) {
    form.append("file", options.file);
  }
  if (options.source_file_id !== undefined) {
    form.append("source_file_id", options.source_file_id);
  }
  if (options.source_provider !== undefined) {
    form.append("source_provider", options.source_provider);
  }
  if (options.source_connection_key !== undefined) {
    form.append("source_connection_key", options.source_connection_key);
  }
  if (options.source_presigned_key !== undefined) {
    form.append("source_presigned_key", options.source_presigned_key);
  }
  if (options.sheet_names !== undefined && options.sheet_names.length > 0) {
    form.append("sheet_names", options.sheet_names.join(","));
  }

  return apiClient.request<SpreadsheetPreviewResponse>(
    "/enterprise/connectors/preview-spreadsheet",
    {
      method: "POST",
      body: form,
    },
  );
}

// ─── Import ─────────────────────────────────────────────────────────────────

export interface ImportSpreadsheetOptions {
  /** Required — human-readable name for the resulting connector row. */
  readonly connector_name: string;
  /** Upload-mode: local ``.xlsx`` / ``.csv``. */
  readonly file?: File;
  /** Cloud-mode: provider file id (Drive ``fileId`` / OneDrive ``driveItemId``). */
  readonly source_file_id?: string;
  /** Cloud-mode: which Drive provider holds the token to borrow. */
  readonly source_provider?: SpreadsheetSourceProvider;
  /**
   * Cloud-mode: optional explicit connection key. BE defaults to the
   * tenant's only ``source_provider`` connection — required only when the
   * user holds multiple accounts on the same provider.
   */
  readonly source_connection_key?: string;
  /**
   * Optional sheet subset (Excel only — CSV is single-sheet). Comma-joined
   * at the wire boundary; absent ⇒ BE imports all sheets.
   */
  readonly sheet_names?: readonly string[];
  /**
   * Chunked-upload mode (giugno 12, 2026): after the FE drove the
   * multipart upload to completion via ``/spreadsheet/multipart/*``,
   * the resulting ``object_key`` is passed here. BE pulls the bytes
   * from MinIO with server-side credentials — no body travels through
   * this request, sidestepping the GFE 32 MB body cap. See
   * ``CHUNKED MULTIPART UPLOAD`` block in BE
   * ``enterprise_connectors.py``.
   */
  readonly source_presigned_key?: string;
}

/**
 * BE returns 202 since June 13, 2026: the actual parse + DB load + LLM
 * profiling now run inside ``SpreadsheetIngestionWorkflow`` on the
 * Temporal ingestion queue. The endpoint envelope mirrors a regular
 * connector card (so the optimistic "pending" tile can be rendered off
 * the same shape) plus a ``workflow_id`` for ops follow-up.
 *
 * Progress + completion events ride the existing
 * ``ws_bcast:drive_sync`` channel — ``SyncContext`` already wires the
 * generic "ingestion_progress"/"ingestion_complete" payloads. The FE just
 * needs to discriminate ``source: "spreadsheet"`` to flip the card from
 * ``pending`` to ``healthy`` (or ``unhealthy`` on failure).
 *
 * ``connector_id`` kept as a back-compat alias of ``id`` so call sites
 * that previously read it (drawer success copy, react-query
 * invalidations) keep working with a one-line wire mapping.
 */
export interface ImportSpreadsheetResponse {
  readonly id: string;
  readonly name: string;
  readonly connector_type: "spreadsheet";
  readonly status: "pending" | "healthy" | "unhealthy" | "unknown";
  readonly workflow_id: string;
  readonly created_at: string | null;
  /** @deprecated use ``id``; kept for back-compat with pre-async call sites. */
  readonly connector_id?: string;
}

/**
 * Fire the multipart POST. Built on ``apiClient.request<T>()`` (NOT
 * ``apiClient.post()``) because the latter forces ``Content-Type:
 * application/json``; multipart needs the browser to inject the
 * boundary itself — same trick ``packages/core/src/api/endpoints/uploads.ts``
 * uses for chat-image / chat-file uploads.
 */
export async function importSpreadsheet(
  options: ImportSpreadsheetOptions,
): Promise<ImportSpreadsheetResponse> {
  const form = new FormData();
  form.append("connector_name", options.connector_name);

  if (options.file !== undefined) {
    form.append("file", options.file);
  }
  if (options.source_file_id !== undefined) {
    form.append("source_file_id", options.source_file_id);
  }
  if (options.source_provider !== undefined) {
    form.append("source_provider", options.source_provider);
  }
  if (options.source_connection_key !== undefined) {
    form.append("source_connection_key", options.source_connection_key);
  }
  if (options.source_presigned_key !== undefined) {
    form.append("source_presigned_key", options.source_presigned_key);
  }
  if (options.sheet_names !== undefined && options.sheet_names.length > 0) {
    form.append("sheet_names", options.sheet_names.join(","));
  }

  const raw = await apiClient.request<ImportSpreadsheetResponse>(
    "/enterprise/connectors/import-spreadsheet",
    {
      method: "POST",
      body: form,
      timeout: IMPORT_SPREADSHEET_TIMEOUT_MS,
    },
  );
  // The 202 envelope carries ``id``; some pre-async call sites still read
  // ``connector_id``. Populate the legacy alias inline so we don't have
  // to thread the migration through every component in one PR.
  return { ...raw, connector_id: raw.id };
}

// Endpoint is async since June 13, 2026 — returns 202 in tens of
// milliseconds after staging the file to MinIO + starting the workflow.
// Keep a generous ceiling for the upload + start_workflow round trip on
// slow links (mobile, ferrovie, etc.), not for the ingest itself.
const IMPORT_SPREADSHEET_TIMEOUT_MS = 120_000;

// ─── Chunked multipart upload (BE-proxy → MinIO S3 multipart) ─────────
//
// SOTA giugno 2026 pattern per uploadare file > 32 MB su deployment
// dietro Google Cloud HTTP(S) LB. Tre step + abort:
//   1. ``startMultipartSpreadsheetUpload`` → ``upload_id`` + ``object_key`` + ``part_size``
//   2. ``uploadSpreadsheetPart`` (× N) → ``{etag, part_number}``
//   3. ``completeSpreadsheetMultipart`` → finalizza
//   4. ``abortSpreadsheetMultipart`` → cleanup su cancel/errore
// L'``object_key`` finale alimenta ``importSpreadsheet({source_presigned_key})``.

export interface MultipartStartParams {
  readonly filename: string;
  readonly size_bytes: number;
  readonly content_type?: string;
}

export interface MultipartStartResponse {
  readonly upload_id: string;
  readonly object_key: string;
  /** Chunk size (bytes) imposto dal BE — il FE clamp lo slice del Blob a questo valore. */
  readonly part_size: number;
  /** Max parti ammesse — limite S3 (10_000). */
  readonly max_parts: number;
}

export async function startMultipartSpreadsheetUpload(
  params: MultipartStartParams,
): Promise<MultipartStartResponse> {
  return apiClient.post<MultipartStartResponse>(
    "/enterprise/connectors/spreadsheet/multipart/start",
    params,
  );
}

export interface MultipartPartParams {
  readonly upload_id: string;
  readonly object_key: string;
  readonly part_number: number;
  readonly chunk: Blob;
  /** Optional ``AbortSignal`` — cancella il PUT di QUESTO chunk. */
  readonly signal?: AbortSignal;
}

export interface MultipartPartResponse {
  readonly part_number: number;
  readonly etag: string;
}

export async function uploadSpreadsheetPart(
  params: MultipartPartParams,
): Promise<MultipartPartResponse> {
  const form = new FormData();
  form.append("upload_id", params.upload_id);
  form.append("object_key", params.object_key);
  form.append("part_number", String(params.part_number));
  form.append("file", params.chunk);
  return apiClient.request<MultipartPartResponse>(
    "/enterprise/connectors/spreadsheet/multipart/part",
    {
      method: "POST",
      body: form,
      signal: params.signal,
    },
  );
}

export interface MultipartCompleteParams {
  readonly upload_id: string;
  readonly object_key: string;
  readonly parts: ReadonlyArray<{ part_number: number; etag: string }>;
}

export interface MultipartCompleteResponse {
  readonly object_key: string;
  readonly size_bytes: number;
}

export async function completeSpreadsheetMultipart(
  params: MultipartCompleteParams,
): Promise<MultipartCompleteResponse> {
  return apiClient.post<MultipartCompleteResponse>(
    "/enterprise/connectors/spreadsheet/multipart/complete",
    params,
  );
}

export interface MultipartAbortParams {
  readonly upload_id: string;
  readonly object_key: string;
}

export async function abortSpreadsheetMultipart(
  params: MultipartAbortParams,
): Promise<{ ok: boolean }> {
  return apiClient.post<{ ok: boolean }>(
    "/enterprise/connectors/spreadsheet/multipart/abort",
    params,
  );
}
